// src/pages/LandingPage.tsx
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LuArrowRight,
  LuBot,
  LuGithub,
  LuMail,
  LuPlay,
  LuSearch,
  LuSettings2,
  LuShieldCheck,
  LuTable2,
  LuTimer,
  LuWrench,
} from 'react-icons/lu';
import { SidebarCollapseContext } from '../contexts/SidebarCollapseContext';
import styles from './LandingPage.module.css';

const services = [
  {
    icon: <LuWrench />,
    title: 'Automation Sprint',
    summary: 'Fix one workflow fast.',
    details: 'Replace manual steps with a small workflow that saves time immediately.',
    link: '/contact',
  },
  {
    icon: <LuTable2 />,
    title: 'Internal Tool Build',
    summary: 'Dashboards, trackers, and admin tools.',
    details: 'Build a simple tool your team can use without spreadsheet chaos.',
    link: '/contact',
  },
  {
    icon: <LuSearch />,
    title: 'Systems Audit',
    summary: 'Find inefficiencies and fix the worst ones first.',
    details: 'Review the current process, identify bottlenecks, and leave with a clear plan.',
    link: '/contact',
  },
];

const beforeAfter = [
  {
    before: 'Manual Excel tracking',
    after: 'Automated dashboard with live updates',
  },
  {
    before: 'Copying form data into email threads',
    after: 'Auto-routed requests with clear ownership',
  },
  {
    before: 'Chasing updates in chat',
    after: 'One simple board everyone can check',
  },
];

const proofItems = [
  {
    title: 'Understand',
    text: 'We map the workflow, the friction, and the outcome before we build.',
  },
  {
    title: 'Build',
    text: 'We turn the clearest opportunity into a focused automation or internal tool.',
  },
  {
    title: 'Improve',
    text: 'We leave your team with a practical system that can keep getting better.',
  },
];

const processSteps = [
  'Understand the workflow and the opportunity.',
  'Design and build the right-sized solution.',
  'Support adoption and keep improving the system.',
];

const AutoPlayVideo: React.FC<React.VideoHTMLAttributes<HTMLVideoElement>> = ({ children, ...props }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const playVideo = () => {
      video.play().catch(() => undefined);
    };

    video.addEventListener('canplay', playVideo);
    playVideo();
    return () => video.removeEventListener('canplay', playVideo);
  }, []);

  return <video ref={videoRef} autoPlay muted loop playsInline preload="auto" {...props}>{children}</video>;
};

export const LandingPage: React.FC = () => {
  const { isSidebarCollapsed } = useContext(SidebarCollapseContext);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={`${styles.landingPage} ${isSidebarCollapsed ? styles.collapsed : ''}`}>
      <header className={`${styles.hero} ${isVisible ? styles.fadeIn : ''}`}>
        <div className={styles.heroContent}>
          <div className={styles.heroLogoFrame}>
            <img src="/logo.jpeg" alt="PrismaOps" />
          </div>
          <div className={styles.heroBadge}>
            <LuTimer className={styles.badgeIcon} />
            <span>AI, automation and software systems</span>
          </div>
          <h1 className={styles.heroTitle}>
            We design and build practical digital systems that make your business easier to run.
          </h1>
          <p className={styles.heroSubtitle}>
            PrismaOps helps growing businesses connect their processes, automate repetitive work, and turn scattered operations into clear systems.
          </p>
          <div className={styles.heroButtons}>
            <Link to="/contact" className={styles.ctaButton}>
              Talk to PrismaOps
              <LuArrowRight className={styles.buttonIcon} />
            </Link>
            <a href="#services" className={styles.secondaryButton}>
              Explore our solutions
            </a>
          </div>

          <div className={styles.heroVisual} aria-label="PrismaOps automation workspace preview">
            <div className={styles.visualChrome}>
              <span className={styles.windowDots}><i /><i /><i /></span>
              <span className={styles.visualPath}>prismaops / live-workflows</span>
              <span className={styles.liveStatus}><span /> live system</span>
            </div>
            <div className={styles.visualBody}>
              <div className={styles.visualSidebar}>
                <span className={styles.visualLogo}>P</span>
                <span className={styles.sidebarLine} /><span className={styles.sidebarLine} />
                <span className={styles.sidebarLine} /><span className={styles.sidebarLine} />
              </div>
              <div className={styles.visualMain}>
                <div className={styles.visualHeading}>
                  <div><span className={styles.miniLabel}>Operations command centre</span><strong>Good morning, team.</strong></div>
                  <button type="button" className={styles.visualPlay}><LuPlay /></button>
                </div>
                <div className={styles.visualStats}>
                          <div><span>Active workflows</span><strong>Live</strong><em>monitoring</em></div>
                          <div><span>Tasks automated</span><strong>Ready</strong><em>to run</em></div>
                  <div><span>System status</span><strong className={styles.statusText}>Healthy</strong><em><LuShieldCheck /></em></div>
                </div>
                <div className={styles.workflowPanel}>
                  <div className={styles.panelTop}><span>Workflow activity</span><span>Last 7 days <LuArrowRight /></span></div>
                  <div className={styles.workflowGraph}><span className={styles.graphLine} /><span className={styles.graphLine} /><span className={styles.graphLine} /><span className={styles.graphPulse} /></div>
                  <div className={styles.workflowNodes}>
                    <div><span className={styles.nodeIcon}><LuBot /></span><span><b>New request</b><small>Trigger</small></span><i>Done</i></div>
                    <div><span className={styles.nodeIcon}><LuSettings2 /></span><span><b>Qualify + route</b><small>Automation</small></span><i>Running</i></div>
                    <div><span className={styles.nodeIcon}><LuGithub /></span><span><b>Create task</b><small>Integration</small></span><i>Queued</i></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.mediaRail}>
            <div className={styles.mediaVideo}>
              <AutoPlayVideo poster="/Automation graphic.jpeg">
                <source src="/Ai%20Mascot.mp4" type="video/mp4" />
              </AutoPlayVideo>
              <div className={styles.mediaCaption}><span>AI systems</span><strong>Designed to move work forward.</strong></div>
            </div>
            <div className={styles.mediaImage}><img src="/AdobeStock_362577635.jpeg" alt="Technology and systems integration illustration" /><span>Systems integration</span></div>
            <div className={styles.mediaImage}><img src="/Automations.jpeg" alt="Automation workflow interface" /><span>Workflow orchestration</span></div>
            <div className={styles.mediaImage}><img src="/Automation%20graphic.jpeg" alt="PrismaOps automation graphic" /><span>Connected operations</span></div>
          </div>

          <div className={styles.proofStrip}>
            {proofItems.map((item) => (
              <div key={item.title} className={styles.proofCard}>
                <div className={styles.proofIcon}>
                  <LuBot />
                </div>
                <h3 className={styles.proofTitle}>{item.title}</h3>
                <p className={styles.proofText}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className={styles.mainContent}>
        <section className={styles.problemSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionEyebrow}>Why PrismaOps</p>
            <h2 className={styles.sectionTitle}>Technology should remove friction, not add another layer of work.</h2>
            <p className={styles.sectionCopy}>
              We work hand in hand with your team to find the highest-value improvement, then make it simple enough to use every day.
            </p>
          </div>

          <div className={styles.problemGrid}>
            <div className={styles.problemCard}>
              <LuSettings2 className={styles.problemIcon} />
              <h3>Practical by design</h3>
              <p>Focused systems that fit the way your people already work.</p>
            </div>
            <div className={styles.problemCard}>
              <LuSearch className={styles.problemIcon} />
              <h3>Built around outcomes</h3>
              <p>Every build starts with a business problem and a useful result.</p>
            </div>
            <div className={styles.problemCard}>
              <LuTimer className={styles.problemIcon} />
              <h3>Ready to evolve</h3>
              <p>Clean foundations that can grow as your operations and ambitions grow.</p>
            </div>
          </div>
        </section>

        <section id="services" className={styles.servicesSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionEyebrow}>Services & solutions</p>
            <h2 className={styles.sectionTitle}>Digital systems that connect, modernise, and automate.</h2>
            <p className={styles.sectionCopy}>From a focused workflow fix to a complete internal platform, we build technology around the work that matters.</p>
          </div>

          <div className={styles.cardsContainer}>
            {services.map((service, index) => (
              <Link key={service.title} to={service.link} className={styles.serviceCard} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className={styles.serviceHeader}>
                  <div className={styles.cardIcon}>{service.icon}</div>
                </div>
                <h3 className={styles.cardTitle}>{service.title}</h3>
                <p className={styles.cardOutcome}>{service.summary}</p>
                <p className={styles.cardDescription}>{service.details}</p>
                <span className={styles.cardLink}>
                  Show me what I can automate <LuArrowRight className={styles.linkIcon} />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className={styles.examplesSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionEyebrow}>Before / after</p>
            <h2 className={styles.sectionTitle}>Simple examples that show the outcome.</h2>
          </div>
          <div className={styles.examplesGrid}>
            {beforeAfter.map((item, index) => (
              <div key={item.before} className={styles.exampleCard}>
                <div className={`${styles.automationMockup} ${styles[`mockup${index + 1}`]}`} aria-hidden="true">
                  {index === 0 && <><div className={styles.sheetRows}><i /><i /><i /><i /></div><LuArrowRight className={styles.mockupArrow} /><div className={styles.dashboardBars}><i /><i /><i /><i /></div></>}
                  {index === 1 && <><div className={styles.inboxNode}><LuMail /></div><div className={styles.routeLine} /><div className={styles.routeNodes}><i /><i /><i /></div></>}
                  {index === 2 && <><div className={styles.boardColumn}><i /><i /><i /></div><div className={styles.boardColumn}><i /><i /></div><div className={styles.boardColumn}><i /><i /><i /></div></>}
                </div>
                <div className={styles.exampleComparison}>
                  <div><span className={styles.exampleLabel}>Before</span><p className={styles.exampleText}>{item.before}</p></div>
                  <LuArrowRight className={styles.exampleArrow} />
                  <div><span className={`${styles.exampleLabel} ${styles.afterLabel}`}>After</span><p className={styles.exampleText}>{item.after}</p></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.proofSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionEyebrow}>Our approach</p>
            <h2 className={styles.sectionTitle}>A clear path from messy process to working system.</h2>
            <p className={styles.sectionCopy}>We combine strategic thinking, thoughtful design, and hands-on implementation without unnecessary complexity.</p>
          </div>
          <div className={styles.proofGrid}>
            {proofItems.map((item) => (
              <div key={item.title} className={styles.proofCard}>
                <div className={styles.proofIcon}>
                  <LuBot />
                </div>
                <h3 className={styles.proofTitle}>{item.title}</h3>
                <p className={styles.proofText}>{item.text}</p>
              </div>
            ))}
          </div>
          <div className={styles.videoBanner}>
            <AutoPlayVideo poster="/Automation graphic.jpeg">
              <source src="/stats.mp4" type="video/mp4" />
            </AutoPlayVideo>
            <div><span className={styles.sectionEyebrow}>Operational clarity</span><h3>See the signal inside the noise.</h3><p>We turn disconnected tasks, requests, and updates into systems your team can understand and use.</p></div>
          </div>
        </section>

        <section className={styles.processSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionEyebrow}>Delivery philosophy</p>
            <h2 className={styles.sectionTitle}>Collaborative, focused, and built for momentum.</h2>
          </div>
          <div className={styles.processGrid}>
            {processSteps.map((step, index) => (
              <div key={step} className={styles.processCard}>
                <div className={styles.processNumber}>0{index + 1}</div>
                <p className={styles.processText}>{step}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.finalCta}>
          <div className={styles.ctaContent}>
            <p className={styles.sectionEyebrow}>Start a conversation</p>
            <h2 className={styles.ctaTitle}>Let’s build a better way to work.</h2>
            <p className={styles.ctaText}>
              Tell us where work slows down. We will help you identify the right next step, from a systems audit to a working digital tool.
            </p>
            <Link to="/contact" className={styles.ctaButton}>
              Talk to PrismaOps
              <LuArrowRight className={styles.buttonIcon} />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};
